import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonService } from 'src/app/shared/services/common.service';
import { ITaskDetail, ManagementService, TaskBackendFilters } from './management.service';
import { ActivatedRoute, Router } from '@angular/router';
import { BreakPointService } from 'src/app/shared/services/break-point.service';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { TeamMember } from '../team/team-member.model';
import { avatarColorFor } from '../../shared/utils/avatar-color';
import { TeamService } from '../team/team.service';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import {
  DEFAULT_DETAIL_SECTIONS,
  DetailSection,
  displayTaskStatus,
  ICardData,
  ITaskActivity,
  ITaskApi,
  ITaskChecklistItem,
  ITaskComment,
  ITaskData,
  ITaskDetailData,
  ITaskDocument,
  ITopicListItem,
  isCompletedTaskStatus,
  memberDisplayName,
  priorityClass as taskPriorityClass,
  priorityLabel as formatPriorityLabel,
  TASK_ASSIGNEE_PREVIEW_LIMIT,
  TASK_STATUS_ORDER,
  TaskCardUi,
  TaskStatusUi,
  TaskView,
  taskStatusColor,
  taskStatusSlug,
  taskStatusSoftColor,
} from './task-management.models';
import {
  PortalSortDirection,
  PortalTableColumn,
  PortalTableSortChange,
} from 'src/app/shared/components/portal-table/portal-table.model';

type ListSortKey = 'task' | 'status' | 'priority' | 'dueDate' | 'assignees' | 'assets';
type SortDirection = 'asc' | 'desc';
type CalendarMode = 'month' | 'week';
interface CalendarDay {
  date: Date;
  key: string;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  tasks: ICardData[];
}

@Component({
  selector: 'app-management',
  templateUrl: './management.component.html',
  styleUrls: ['./management.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManagementComponent implements OnDestroy, OnInit {
  @ViewChild('periodPicker') periodPicker?: MatDateRangePicker<unknown>;
  @ViewChild('periodSelect') periodSelect?: MatSelect;

  taskView: TaskView = 'board';
  listSortKey: ListSortKey = 'dueDate';
  listSortDirection: SortDirection = 'asc';
  listRowsPerPage = 10;
  listCurrentPage = 1;
  readonly listRowsPerPageOptions = [10, 25, 50];
  readonly taskListColumns: PortalTableColumn[] = [
    { key: 'task', label: 'Task', width: 'minmax(260px, 1.55fr)', sortable: true },
    { key: 'status', label: 'Status', width: 'minmax(130px, 0.78fr)', align: 'center', sortable: true },
    { key: 'priority', label: 'Priority', width: 'minmax(120px, 0.72fr)', align: 'center', sortable: true },
    { key: 'dueDate', label: 'Due date', width: 'minmax(170px, 0.95fr)', align: 'center', sortable: true },
    { key: 'assignees', label: 'Assignees', width: 'minmax(140px, 0.78fr)', align: 'center', sortable: true },
    { key: 'assets', label: 'Assets', width: 'minmax(120px, 0.62fr)', align: 'right', sortable: true },
  ];
  readonly calendarWeekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  readonly monthCalendarTaskLimit = 2;
  readonly weekCalendarTaskLimit = 6;
  calendarDate = new Date();
  calendarMode: CalendarMode = 'month';
  selectedCalendarDateKey = this.dateKey(new Date());
  search = '';
  topicList: ITopicListItem[] = [];
  taskDatas: ITaskData[] = [];
  vehicleId = '';
  gpId = '';
  isLoading = false;
  isRefreshingTasks = false;
  isTaskDragging = false;
  activeDragSourceStatus: string | null = null;
  persistedTaskDatas: ITaskData[] = [];
  showNoTopics = false;
  taskLoadError: { title: string; description: string } | null = null;
  suggestTaskAvailable = false;
  assigneeList: TeamMember[] = [];
  assignees: TeamMember[] = [];
  filterSource: 'all' | 'native' | 'clickup' = 'all';
  filterStatuses: string[] = [];
  filterPriorities: string[] = [];
  filterAssigneeIds: string[] = [];
  filterDueFrom = '';
  filterDueTo = '';
  filterDueFromDate: unknown | null = null;
  filterDueToDate: unknown | null = null;
  filterPeriod: 'all_time' | 'today' | 'this_week' | 'custom' = 'all_time';
  activeAdvancedFilters = 0;
  private searchTimer?: ReturnType<typeof setTimeout>;
  private periodPickerTimer?: ReturnType<typeof setTimeout>;
  private subscriptions = new Subscription();
  private appliedCustomPeriodKey = '';
  private createTaskDialogRef?: MatDialogRef<TaskDetailsComponent>;
  private taskDetailsDialogRef?: MatDialogRef<TaskDetailsComponent>;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private managementService: ManagementService,
    private teamService: TeamService,
    private commonService: CommonService,
    public breakPointService: BreakPointService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.vehicleId = this.activatedRoute.snapshot.params['vehicleId'];
    this.gpId = this.activatedRoute.snapshot.params['gpId'];
    this.subscriptions.add(
      this.managementService.taskCreatedObservable$.subscribe(() => {
        this.getAllTasks(true);
      })
    );
    this.loadTeamThenTasks();
  }

  ngOnDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    if (this.periodPickerTimer) {
      clearTimeout(this.periodPickerTimer);
    }
    this.createTaskDialogRef?.close();
    this.taskDetailsDialogRef?.close();
    this.subscriptions.unsubscribe();
  }

  loadTeamThenTasks() {
    this.isLoading = true;
    this.cdr.markForCheck();
    this.teamService.getFirmTeam().subscribe({
      next: members => {
        this.assigneeList = members;
        this.cdr.markForCheck();
      },
      error: () => {
        this.assigneeList = [];
        this.cdr.markForCheck();
        this.getAllTasks();
      },
      complete: () => {
        this.getAllTasks();
      },
    });
  }

  avatarColorFor(id: string): string {
    return avatarColorFor(id);
  }

  sortedTasks(tasks: ITaskApi[]) {
    const priorityOrder: Record<string, number> = {
      critical: 1,
      high: 2,
      medium: 3,
      low: 4,
    };

    return [...tasks].sort((a, b) => {
      const priorityComparison =
        (priorityOrder[(a.priority || '').toLowerCase()] ?? 99) -
        (priorityOrder[(b.priority || '').toLowerCase()] ?? 99);

      if (priorityComparison !== 0) {
        return priorityComparison;
      }

      if (!a.dueAt && !b.dueAt) {
        return b.id.localeCompare(a.id);
      } else if (!a.dueAt) {
        return 1;
      } else if (!b.dueAt) {
        return -1;
      }

      const dateComparison = new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();

      if (dateComparison !== 0) {
        return dateComparison;
      }

      return b.id.localeCompare(a.id);
    });
  }

  getAllTasks(silent = false) {
    if (silent) {
      this.isRefreshingTasks = true;
    } else {
      this.isLoading = true;
    }
    this.cdr.markForCheck();
    this.managementService
      .getAllTasks(this.vehicleId, this.backendFilters(), this.gpId)
      .subscribe({
        next: response => {
          const data = response?.data;
          this.showNoTopics = false;
          this.taskLoadError = null;
          this.topicList = data?.topics ?? [];
          this.taskDatas = this.transformTasks(this.sortedTasks(data?.tasks ?? []));
          this.persistedTaskDatas = this.cloneTaskLanes(this.taskDatas);
          this.suggestTaskAvailable = !!data?.suggestTaskAvailable;
          this.cdr.markForCheck();
        },
        error: error => {
          const message = this.errorMessage(error);
          const code = this.errorCode(error);
          if (message.includes('Vehicle does not exist')) {
            this.showNoTopics = true;
          }
          this.taskLoadError = this.taskLoadErrorCopy(error, message, code);
          this.topicList = [];
          this.taskDatas = [];
          this.persistedTaskDatas = [];
          this.suggestTaskAvailable = false;
          this.isLoading = false;
          this.isRefreshingTasks = false;
          this.cdr.markForCheck();
        },
        complete: () => {
          this.isLoading = false;
          this.isRefreshingTasks = false;
          this.cdr.markForCheck();
        },
      });
  }

  retryLoadTasks() {
    this.taskLoadError = null;
    this.getAllTasks();
  }

  private taskLoadErrorCopy(error: unknown, message: string, code?: string) {
    const status = (error as { status?: number } | null)?.status;
    if (code === 'FUND_ACCESS_DENIED' || status === 403) {
      return {
        title: 'You do not have access to this fund',
        description: 'Tasks for this fund are managed in SPIKE, but your account has not been granted access to this fund yet. Ask an administrator to update your access, then try again.',
      };
    }
    if (code === 'FUND_NOT_LINKED') {
      return {
        title: 'This fund is not connected to SPIKE yet',
        description: 'Tasks are managed in SPIKE. This GP Portal fund has not been linked to a SPIKE fund yet, so there are no tasks to show or create here.',
      };
    }
    if (code === 'FUND_NOT_FOUND' || status === 404) {
      return {
        title: 'This fund could not be found in SPIKE',
        description: 'The SPIKE fund may have been removed or the connection needs to be updated. Ask an administrator to check the fund connection.',
      };
    }
    return {
      title: 'Tasks could not be loaded from SPIKE',
      description: 'Please try again. If the issue continues, contact SPIKE support.',
    };
  }

  private errorMessage(error: unknown): string {
    const body = (error as { error?: { message?: string | string[] } } | null)?.error;
    const message = body?.message ?? (error as { message?: string } | null)?.message;
    return Array.isArray(message) ? message.join(', ') : message ?? '';
  }

  private errorCode(error: unknown): string | undefined {
    const body = (error as {
      error?: { error?: { code?: string }; code?: string };
      code?: string;
    } | null)?.error;
    return body?.error?.code ?? body?.code ?? (error as { code?: string } | null)?.code;
  }

  getTaskDetails(cardData: ICardData, status: string) {
    if (this.taskDetailsDialogRef || this.createTaskDialogRef) {
      return;
    }

    this.managementService.closeDrawer();
    this.managementService.setTaskDetails({
      ...cardData,
      status,
      vehicleId: this.vehicleId,
      gpId: this.gpId,
      assigneeList: this.assigneeList,
      isSuggest: false,
      isDialog: true,
    });

    this.taskDetailsDialogRef = this.dialog.open(TaskDetailsComponent, {
      width: 'min(760px, calc(100vw - 32px))',
      maxWidth: 'calc(100vw - 32px)',
      height: 'min(760px, calc(100vh - 32px))',
      maxHeight: 'calc(100vh - 32px)',
      panelClass: 'task-details-dialog-panel',
      backdropClass: 'create-task-dialog-backdrop',
      ariaLabel: 'Task details',
      autoFocus: 'first-tabbable',
    });
    this.taskDetailsDialogRef.afterClosed().subscribe(() => {
      this.taskDetailsDialogRef = undefined;
      this.managementService.setTaskDetails(null);
    });
  }

  openTaskListRow(row: object) {
    const task = row as ICardData;
    this.getTaskDetails(task, task.ui.status);
  }

  formatDate(dateString: string) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  }

  listTasks(): ICardData[] {
    return this.taskDatas.flatMap(lane => lane.tasks);
  }

  sortedListTasks(): ICardData[] {
    const direction = this.listSortDirection === 'asc' ? 1 : -1;
    return [...this.listTasks()].sort((firstTask, secondTask) =>
      this.compareListTasks(firstTask, secondTask, this.listSortKey) * direction
    );
  }

  visibleListTasks(): ICardData[] {
    const tasks = this.sortedListTasks();
    const startIndex = (this.normalizedListCurrentPage() - 1) * this.listRowsPerPage;
    return tasks.slice(startIndex, startIndex + this.listRowsPerPage);
  }

  get listPortalSortDirection(): PortalSortDirection {
    return this.listSortDirection;
  }

  listDueLabel(task: ICardData): string {
    if (!task.dueAt) {
      return task.ui.dueLabel || 'No due date';
    }
    if (task.ui.isDueSoon) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const due = new Date(task.dueAt);
      due.setHours(0, 0, 0, 0);
      const days = Math.ceil((due.getTime() - today.getTime()) / 86400000);
      if (days >= 0 && days <= 7) {
        return days === 0 ? 'Due today' : `Due in ${days} day${days === 1 ? '' : 's'}`;
      }
    }
    return this.formatDate(task.dueAt);
  }

  listTotalPages(): number {
    return Math.max(1, Math.ceil(this.listTasks().length / this.listRowsPerPage));
  }

  listCurrentPageValue(): number {
    return this.normalizedListCurrentPage();
  }

  setListRowsPerPage(rowsPerPage: number) {
    this.listRowsPerPage = rowsPerPage;
    this.listCurrentPage = 1;
    this.cdr.markForCheck();
  }

  setListPage(page: number) {
    this.listCurrentPage = Math.min(Math.max(page, 1), this.listTotalPages());
    this.cdr.markForCheck();
  }

  setListPageIndex(pageIndex: number) {
    this.setListPage(pageIndex + 1);
  }

  portalListSortChange(event: PortalTableSortChange) {
    this.listSortKey = event.active as ListSortKey;
    this.listSortDirection = (event.direction || 'asc') as SortDirection;
    this.listCurrentPage = 1;
    this.cdr.markForCheck();
  }

  taskPriorityTone(task: ICardData): 'success' | 'warning' | 'danger' | 'neutral' {
    const priority = (task.priority || '').toUpperCase();
    if (priority === 'HIGH') {
      return 'danger';
    }
    if (priority === 'MEDIUM') {
      return 'warning';
    }
    if (priority === 'LOW') {
      return 'success';
    }
    return 'neutral';
  }

  private normalizedListCurrentPage(): number {
    return Math.min(Math.max(this.listCurrentPage, 1), this.listTotalPages());
  }

  private compareListTasks(firstTask: ICardData, secondTask: ICardData, key: ListSortKey): number {
    if (key === 'task') {
      return this.compareText(firstTask.title, secondTask.title);
    }
    if (key === 'status') {
      return this.compareText(firstTask.ui.statusLabel, secondTask.ui.statusLabel);
    }
    if (key === 'priority') {
      return this.priorityRank(firstTask.priority) - this.priorityRank(secondTask.priority);
    }
    if (key === 'dueDate') {
      return this.dueTimestamp(firstTask) - this.dueTimestamp(secondTask);
    }
    if (key === 'assignees') {
      return this.compareText(this.assigneeSortValue(firstTask), this.assigneeSortValue(secondTask));
    }
    return this.assetsCount(firstTask) - this.assetsCount(secondTask);
  }

  private compareText(firstValue: string | null | undefined, secondValue: string | null | undefined): number {
    return (firstValue || '').localeCompare(secondValue || '', undefined, { sensitivity: 'base' });
  }

  private priorityRank(priority: string | null | undefined): number {
    const priorityOrder: Record<string, number> = {
      HIGH: 0,
      MEDIUM: 1,
      LOW: 2,
    };
    return priorityOrder[(priority || '').toUpperCase()] ?? 3;
  }

  private dueTimestamp(task: ICardData): number {
    return task.dueAt ? new Date(task.dueAt).getTime() : Number.MAX_SAFE_INTEGER;
  }

  private assigneeSortValue(task: ICardData): string {
    return (task.ui.assignees || [])
      .map(assignee => memberDisplayName(assignee))
      .join(', ');
  }

  private assetsCount(task: ICardData): number {
    return (task.documentsCount || 0) + (task.commentsCount || 0);
  }

  setTaskView(view: TaskView) {
    if (this.taskView !== view) {
      this.taskView = view;
      this.cdr.markForCheck();
    }
  }

  calendarMonthLabel(): string {
    if (this.calendarMode === 'week') {
      const week = this.calendarWeek();
      const firstDay = week[0]?.date;
      const lastDay = week[week.length - 1]?.date;
      if (!firstDay || !lastDay) {
        return this.calendarDate.toLocaleString('en-US', { month: 'long', year: 'numeric' });
      }
      const sameMonth = firstDay.getMonth() === lastDay.getMonth()
        && firstDay.getFullYear() === lastDay.getFullYear();
      const firstLabel = firstDay.toLocaleString('en-US', { month: 'short', day: 'numeric' });
      const lastLabel = lastDay.toLocaleString('en-US', {
        month: sameMonth ? undefined : 'short',
        day: 'numeric',
        year: 'numeric',
      });
      return `${firstLabel} - ${lastLabel}`;
    }
    return this.calendarDate.toLocaleString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  }

  calendarRows(): CalendarDay[][] {
    return this.calendarMode === 'week'
      ? [this.calendarWeek()]
      : this.calendarWeeks();
  }

  calendarWeeks(): CalendarDay[][] {
    const monthStart = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth(), 1);
    const gridStart = new Date(monthStart);
    gridStart.setDate(monthStart.getDate() - monthStart.getDay());

    const taskMap = this.calendarTaskMap();
    const todayKey = this.dateKey(new Date());
    const weeks: CalendarDay[][] = [];

    for (let weekIndex = 0; weekIndex < 6; weekIndex += 1) {
      const week: CalendarDay[] = [];
      for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
        const date = new Date(gridStart);
        date.setDate(gridStart.getDate() + weekIndex * 7 + dayIndex);
        const key = this.dateKey(date);
        week.push({
          date,
          key,
          dayNumber: date.getDate(),
          isCurrentMonth: date.getMonth() === this.calendarDate.getMonth(),
          isToday: key === todayKey,
          isSelected: key === this.selectedCalendarDateKey,
          tasks: taskMap.get(key) ?? [],
        });
      }
      weeks.push(week);
    }

    return weeks;
  }

  previousCalendarMonth() {
    this.shiftCalendarPeriod(-1);
    this.cdr.markForCheck();
  }

  nextCalendarMonth() {
    this.shiftCalendarPeriod(1);
    this.cdr.markForCheck();
  }

  goToCurrentCalendarMonth() {
    this.calendarDate = new Date();
    this.selectedCalendarDateKey = this.dateKey(this.calendarDate);
    this.cdr.markForCheck();
  }

  setCalendarMode(mode: CalendarMode) {
    if (this.calendarMode === mode) {
      return;
    }
    this.calendarMode = mode;
    this.cdr.markForCheck();
  }

  selectCalendarDay(day: CalendarDay) {
    this.calendarDate = new Date(day.date);
    this.selectedCalendarDateKey = day.key;
    this.cdr.markForCheck();
  }

  selectedCalendarDateLabel(): string {
    const date = this.selectedCalendarDate();
    return date.toLocaleString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  }

  selectedCalendarTasks(): ICardData[] {
    const selectedKey = this.selectedCalendarDateKey || this.dateKey(new Date());
    return this.calendarTaskMap().get(selectedKey) ?? [];
  }

  calendarMonthTaskCount(): number {
    return this.listTasks().filter(task => {
      if (!task.dueAt) {
        return false;
      }
      const dueDate = new Date(task.dueAt);
      return dueDate.getMonth() === this.calendarDate.getMonth()
        && dueDate.getFullYear() === this.calendarDate.getFullYear();
    }).length;
  }

  calendarNoDueTasks(): ICardData[] {
    return this.listTasks()
      .filter(task => !task.dueAt)
      .slice(0, 6);
  }

  calendarOverflowCount(day: CalendarDay): number {
    return Math.max(day.tasks.length - this.calendarVisibleTasks(day).length, 0);
  }

  calendarVisibleTasks(day: CalendarDay): ICardData[] {
    if (this.calendarMode === 'month' && day.tasks.length > this.monthCalendarTaskLimit) {
      return day.tasks.slice(0, 1);
    }
    return day.tasks.slice(0, this.calendarTaskLimit());
  }

  openCalendarTask(event: Event, cardData: ICardData) {
    event.stopPropagation();
    this.getTaskDetails(cardData, cardData.ui.status);
  }

  trackByCalendarWeek(index: number): number {
    return index;
  }

  trackByCalendarDay(_index: number, day: CalendarDay): string {
    return day.key;
  }

  trackByLaneStatus(_index: number, lane: ITaskData): string {
    return lane.status;
  }

  trackByTaskId(_index: number, task: ICardData): string {
    return task.taskId;
  }

  private calendarTaskMap(): Map<string, ICardData[]> {
    return this.listTasks()
      .filter(task => !!task.dueAt)
      .reduce((taskMap, task) => {
        const key = this.dateKey(new Date(task.dueAt as string));
        const tasks = taskMap.get(key) ?? [];
        tasks.push(task);
        tasks.sort((firstTask, secondTask) =>
          this.priorityRank(firstTask.priority) - this.priorityRank(secondTask.priority)
          || this.compareText(firstTask.title, secondTask.title)
        );
        taskMap.set(key, tasks);
        return taskMap;
      }, new Map<string, ICardData[]>());
  }

  private dateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private calendarWeek(): CalendarDay[] {
    const selectedDate = new Date(this.calendarDate);
    const weekStart = new Date(selectedDate);
    weekStart.setDate(selectedDate.getDate() - selectedDate.getDay());

    const taskMap = this.calendarTaskMap();
    const todayKey = this.dateKey(new Date());
    return Array.from({ length: 7 }, (_item, index) => {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + index);
      const key = this.dateKey(date);
      return {
        date,
        key,
        dayNumber: date.getDate(),
        isCurrentMonth: date.getMonth() === this.calendarDate.getMonth(),
        isToday: key === todayKey,
        isSelected: key === this.selectedCalendarDateKey,
        tasks: taskMap.get(key) ?? [],
      };
    });
  }

  private shiftCalendarPeriod(direction: -1 | 1) {
    if (this.calendarMode === 'week') {
      const nextDate = new Date(this.calendarDate);
      nextDate.setDate(this.calendarDate.getDate() + direction * 7);
      this.calendarDate = nextDate;
      this.selectedCalendarDateKey = this.dateKey(nextDate);
      return;
    }
    this.calendarDate = new Date(
      this.calendarDate.getFullYear(),
      this.calendarDate.getMonth() + direction,
      1
    );
    this.selectedCalendarDateKey = this.dateKey(this.calendarDate);
  }

  private calendarTaskLimit(): number {
    return this.calendarMode === 'week'
      ? this.weekCalendarTaskLimit
      : this.monthCalendarTaskLimit;
  }

  private selectedCalendarDate(): Date {
    const [year, month, day] = (this.selectedCalendarDateKey || this.dateKey(new Date()))
      .split('-')
      .map(Number);
    return new Date(year, month - 1, day);
  }

  taskDragStarted(status: string) {
    this.isTaskDragging = true;
    this.activeDragSourceStatus = status;
    this.cdr.markForCheck();
  }

  taskDragEnded() {
    this.clearTaskDragState();
  }

  shouldShowDropSlot(lane: ITaskData): boolean {
    return this.isTaskDragging && lane.status !== this.activeDragSourceStatus;
  }

  trackByMemberId(_index: number, member: TeamMember): string {
    return member.id;
  }

  trackByNumber(_index: number, value: number): number {
    return value;
  }

  transformTasks(inputArray: ITaskApi[]): ITaskData[] {
    const predefinedOrder: string[] = [...TASK_STATUS_ORDER];
    const output = predefinedOrder.reduce((lanes, status) => {
      lanes[status] = { status, tasks: [], ui: this.taskStatusUi(status) };
      return lanes;
    }, {} as Record<string, ITaskData>);
    const topicMap = new Map(this.topicList.map(topic => [topic.id, topic.name]));

    inputArray.forEach(task => {
      const status = predefinedOrder.find(item =>
        item.toLowerCase() === (task.status || '').toLowerCase()
      ) ?? task.status;
      if (!output[status]) {
        output[status] = { status, tasks: [], ui: this.taskStatusUi(status) };
      }
      const assignees = task.assignees?.length
        ? task.assignees
        : task.assignee
          ? [task.assignee]
          : [];
      const dueLabel = task.dueAt
        ? `Due ${this.formatDate(task.dueAt)}`
        : task.dueLabel;
      const newTask: ICardData = {
        topic: topicMap.get(task.topicId) ?? 'General',
        title: task.name,
        description: task.description?.trim() || '',
        taskId: task.id,
        status,
        timeStamp: dueLabel,
        dueAt: task.dueAt,
        priority: task.priority,
        source: task.source,
        documentsCount: task.documentsCount,
        commentsCount: task.commentsCount,
        id: task.id,
        assignee: task.assignee ?? assignees[0] ?? null,
        assignees,
        ui: this.taskCardUi(status, task.priority, dueLabel, task.dueAt, assignees),
      };
      output[status].tasks.push(newTask);
    });

    return Object.values(output).sort((a: ITaskData, b: ITaskData) => {
      const indexA = predefinedOrder.indexOf(a.status);
      const indexB = predefinedOrder.indexOf(b.status);
      if (a.status === 'Completed') return 1;
      if (b.status === 'Completed') return -1;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }

  private cloneTaskLanes(lanes: ITaskData[]): ITaskData[] {
    return lanes.map(lane => ({
      ...lane,
      ui: { ...lane.ui },
      tasks: lane.tasks.map(task => ({
        ...task,
        assignees: [...(task.assignees ?? [])],
        ui: {
          ...task.ui,
          assignees: [...task.ui.assignees],
          visibleAssignees: [...task.ui.visibleAssignees],
        },
      })),
    }));
  }

  private taskStatusUi(status: string): TaskStatusUi {
    return {
      label: displayTaskStatus(status),
      color: taskStatusColor(status),
      softColor: taskStatusSoftColor(status),
      slug: taskStatusSlug(status),
    };
  }

  private taskCardUi(
    status: string,
    priority: string | null | undefined,
    dueLabel: string | null,
    dueAt: string | null,
    assignees: TeamMember[]
  ): TaskCardUi {
    const statusUi = this.taskStatusUi(status);
    return {
      status,
      statusLabel: statusUi.label,
      statusColor: statusUi.color,
      statusSoftColor: statusUi.softColor,
      statusSlug: statusUi.slug,
      dueLabel: dueLabel || 'No due date',
      isDueSoon: this.isTaskDueSoon(dueAt, dueLabel),
      isCompleted: isCompletedTaskStatus(status),
      priorityClass: taskPriorityClass(priority),
      priorityLabel: formatPriorityLabel(priority),
      assignees,
      visibleAssignees: assignees.slice(0, TASK_ASSIGNEE_PREVIEW_LIMIT),
      extraAssigneeCount: Math.max(assignees.length - TASK_ASSIGNEE_PREVIEW_LIMIT, 0),
      assigneeTooltip: assignees.map(assignee => memberDisplayName(assignee)).join(', '),
    };
  }

  private withTaskStatus(task: ICardData, status: string): ICardData {
    const assignees = task.ui?.assignees?.length
      ? task.ui.assignees
      : task.assignees?.length
        ? task.assignees
        : task.assignee
          ? [task.assignee]
          : [];
    return {
      ...task,
      status,
      assignees,
      assignee: task.assignee ?? assignees[0] ?? null,
      ui: this.taskCardUi(status, task.priority, task.timeStamp, task.dueAt, assignees),
    };
  }

  private isTaskDueSoon(dueAt: string | null, dueLabel: string | null): boolean {
    if (dueLabel?.toLowerCase().includes('due in')) {
      return true;
    }
    if (!dueAt) {
      return false;
    }
    const dueDate = new Date(dueAt).getTime();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const twoDaysFromNow = new Date(today);
    twoDaysFromNow.setDate(today.getDate() + 2);
    twoDaysFromNow.setHours(23, 59, 59, 999);
    return dueDate <= twoDaysFromNow.getTime();
  }

  searchChangeHandler() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.searchTimer = setTimeout(() => this.getAllTasks(true), 250);
  }

  clearChangeHandler() {
    this.getAllTasks(true);
  }

  advancedFilterChangeHandler() {
    if (this.filterPriorities.includes('ALL')) {
      this.filterPriorities = [];
    }
    if (this.filterStatuses.includes('ALL')) {
      this.filterStatuses = [];
    }
    this.updateActiveAdvancedFilterCount();
    this.getAllTasks(true);
  }

  isSourceSelected(source: 'native' | 'clickup'): boolean {
    return this.filterSource === 'all' || this.filterSource === source;
  }

  toggleSourceFilter(source: 'native' | 'clickup') {
    const nextNative = source === 'native' ? !this.isSourceSelected('native') : this.isSourceSelected('native');
    const nextClickUp = source === 'clickup' ? !this.isSourceSelected('clickup') : this.isSourceSelected('clickup');

    if (nextNative === nextClickUp) {
      this.filterSource = 'all';
    } else {
      this.filterSource = nextNative ? 'native' : 'clickup';
    }

    this.advancedFilterChangeHandler();
  }

  setFilterAssigneeIds(ids: string[]) {
    this.filterAssigneeIds = ids ?? [];
    const selectedIds = new Set(this.filterAssigneeIds);
    this.assignees = this.assigneeList.filter(assignee => selectedIds.has(assignee.id));
    this.advancedFilterChangeHandler();
  }

  setPeriodFilter(period: 'all_time' | 'today' | 'this_week' | 'custom') {
    this.filterPeriod = period;

    if (period === 'all_time') {
      this.filterDueFrom = '';
      this.filterDueTo = '';
      this.filterDueFromDate = null;
      this.filterDueToDate = null;
      this.appliedCustomPeriodKey = '';
    } else if (period === 'today') {
      const today = new Date();
      this.setFilterDateRange(today, today);
      this.appliedCustomPeriodKey = '';
    } else if (period === 'this_week') {
      const range = this.currentWeekRange();
      this.filterDueFrom = range.from;
      this.filterDueTo = range.to;
      this.filterDueFromDate = this.dateFromInputValue(range.from);
      this.filterDueToDate = this.dateFromInputValue(range.to);
      this.appliedCustomPeriodKey = '';
    } else if (period === 'custom') {
      if (!this.filterDueFrom && !this.filterDueTo) {
        this.filterDueFromDate = null;
        this.filterDueToDate = null;
      }
      this.updateActiveAdvancedFilterCount();
      this.cdr.markForCheck();
      this.openCustomPeriodPicker();
      return;
    } else {
      return;
    }

    this.advancedFilterChangeHandler();
  }

  openCustomPeriodPicker() {
    this.filterPeriod = 'custom';
    this.updateActiveAdvancedFilterCount();
    this.periodSelect?.close();
    if (this.periodPickerTimer) {
      clearTimeout(this.periodPickerTimer);
    }
    this.cdr.detectChanges();
    this.periodPickerTimer = setTimeout(() => {
      this.periodPicker?.open();
      this.cdr.detectChanges();
    }, 180);
  }

  setCustomPeriodDate(type: 'start' | 'end', value: unknown | null) {
    this.filterPeriod = 'custom';
    if (type === 'start') {
      this.filterDueFromDate = value;
      this.filterDueFrom = value ? this.dateInputValue(value) : '';
    } else {
      this.filterDueToDate = value;
      this.filterDueTo = value ? this.dateInputValue(value) : '';
    }

    this.updateActiveAdvancedFilterCount();
    this.cdr.markForCheck();
  }

  applyCustomPeriodFilter() {
    if (this.filterPeriod !== 'custom' || !this.filterDueFrom || !this.filterDueTo) {
      this.updateActiveAdvancedFilterCount();
      this.cdr.markForCheck();
      return;
    }
    const nextKey = `${this.filterDueFrom}|${this.filterDueTo}`;
    if (nextKey === this.appliedCustomPeriodKey) {
      return;
    }
    this.appliedCustomPeriodKey = nextKey;
    this.advancedFilterChangeHandler();
  }

  periodFilterLabel(): string {
    if (this.filterPeriod === 'today') {
      return 'Today';
    }
    if (this.filterPeriod === 'this_week') {
      return 'This Week';
    }
    if (this.filterPeriod === 'custom') {
      return this.customPeriodLabel();
    }
    return 'All Time';
  }

  priorityFilterLabel(): string {
    if (!this.filterPriorities.length) {
      return 'All';
    }
    return this.filterPriorities
      .map(priority => formatPriorityLabel(priority))
      .join(', ');
  }

  statusFilterLabel(): string {
    if (!this.filterStatuses.length) {
      return 'All';
    }
    return this.filterStatuses
      .map(status => displayTaskStatus(status))
      .join(', ');
  }

  assigneeFilterLabel(): string {
    if (!this.assignees.length) {
      return 'All';
    }
    if (this.assignees.length === 1) {
      return memberDisplayName(this.assignees[0]);
    }
    return `${this.assignees.length} people`;
  }

  customPeriodLabel(): string {
    const fromDate = this.toNativeDate(this.filterDueFromDate);
    const toDate = this.toNativeDate(this.filterDueToDate);
    if (!fromDate || !toDate) {
      return 'Select Period';
    }
    const fromMonth = fromDate.toLocaleString('en-US', { month: 'short' });
    const toMonth = toDate.toLocaleString('en-US', { month: 'short' });
    const fromDay = fromDate.getDate();
    const toDay = toDate.getDate();
    if (fromMonth === toMonth) {
      return `${fromMonth} ${fromDay} - ${toDay}`;
    }
    return `${fromMonth} ${fromDay} - ${toMonth} ${toDay}`;
  }

  clearAdvancedFilters() {
    this.filterSource = 'all';
    this.filterStatuses = [];
    this.filterPriorities = [];
    this.filterAssigneeIds = [];
    this.assignees = [];
    this.filterDueFrom = '';
    this.filterDueTo = '';
    this.filterDueFromDate = null;
    this.filterDueToDate = null;
    this.filterPeriod = 'all_time';
    this.appliedCustomPeriodKey = '';
    this.advancedFilterChangeHandler();
  }

  private updateActiveAdvancedFilterCount() {
    this.activeAdvancedFilters = [
      this.filterSource !== 'all',
      this.filterStatuses.length > 0,
      this.filterPriorities.length > 0,
      this.filterAssigneeIds.length > 0,
      this.filterPeriod !== 'all_time',
    ].filter(Boolean).length;
  }

  private backendFilters(): TaskBackendFilters {
    return {
      search: this.search?.trim() || undefined,
      source: this.filterSource !== 'all' ? this.filterSource : undefined,
      priority: this.filterPriorities.length ? this.filterPriorities : undefined,
      status: this.filterStatuses.length ? this.filterStatuses : undefined,
      includePastDue: true,
      memberIds: this.assignees.length ? this.assignees.map(assignee => assignee.id) : undefined,
      dueFrom: this.filterDueFrom ? this.startOfDayIso(this.filterDueFrom) : undefined,
      dueTo: this.filterDueTo ? this.endOfDayIso(this.filterDueTo) : undefined,
    };
  }

  private startOfDayIso(dateValue: string): string {
    const date = this.dateFromInputValue(dateValue);
    date?.setHours(0, 0, 0, 0);
    return (date ?? new Date(dateValue)).toISOString();
  }

  private endOfDayIso(dateValue: string): string {
    const date = this.dateFromInputValue(dateValue) ?? new Date(dateValue);
    date.setHours(23, 59, 59, 999);
    return date.toISOString();
  }

  private currentWeekRange(): { from: string; to: string } {
    const today = new Date();
    const day = today.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    return {
      from: this.dateInputValue(monday),
      to: this.dateInputValue(sunday),
    };
  }

  private dateInputValue(dateValue: unknown): string {
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    const date = this.toNativeDate(dateValue);
    if (!date) {
      return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private dateFromInputValue(value: string): Date | null {
    if (!value) {
      return null;
    }
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  private toNativeDate(value: unknown): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value;
    }
    if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
      const date = value.toDate();
      return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
    }
    if (typeof value === 'string') {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    return null;
  }

  private setFilterDateRange(from: unknown, to: unknown) {
    this.filterDueFromDate = from;
    this.filterDueToDate = to;
    this.filterDueFrom = this.dateInputValue(from);
    this.filterDueTo = this.dateInputValue(to);
  }

  dropTask(event: CdkDragDrop<ICardData[]>, targetLane: ITaskData) {
    const sourceLane = this.taskDatas.find(lane => lane.tasks === event.previousContainer.data);
    if (!sourceLane) {
      this.clearTaskDragState();
      return;
    }

    if (event.previousContainer === event.container) {
      if (event.previousIndex !== event.currentIndex) {
        moveItemInArray(targetLane.tasks, event.previousIndex, event.currentIndex);
        this.syncPersistedLane(targetLane.status, targetLane.tasks);
        this.taskDatas = [...this.taskDatas];
        this.cdr.markForCheck();
      }
      this.clearTaskDragState();
      return;
    }

    const previousStatus = sourceLane.status;
    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );
    const movedTask = this.withTaskStatus(targetLane.tasks[event.currentIndex], targetLane.status);
    targetLane.tasks[event.currentIndex] = movedTask;
    this.syncPersistedLane(sourceLane.status, sourceLane.tasks);
    this.syncPersistedLane(targetLane.status, targetLane.tasks);
    this.taskDatas = [...this.taskDatas];
    this.clearTaskDragState(false);
    this.cdr.markForCheck();

    this.managementService.updateTaskStatus(movedTask.taskId, targetLane.status).subscribe({
      error: () => {
        this.commonService.errorNotification('Failed to update task status');
        this.moveTaskToStatus(movedTask.taskId, previousStatus);
      },
    });
  }

  private clearTaskDragState(markForCheck = true) {
    this.isTaskDragging = false;
    this.activeDragSourceStatus = null;
    if (markForCheck) {
      this.cdr.markForCheck();
    }
  }

  private moveTaskToStatus(taskId: string, status: string) {
    const currentLane = this.taskDatas.find(lane =>
      lane.tasks.some(task => task.taskId === taskId)
    );
    const targetLane = this.taskDatas.find(lane => lane.status === status);
    if (!currentLane || !targetLane) {
      this.getAllTasks();
      return;
    }
    const taskIndex = currentLane.tasks.findIndex(task => task.taskId === taskId);
    if (taskIndex === -1) {
      this.getAllTasks();
      return;
    }
    const [task] = currentLane.tasks.splice(taskIndex, 1);
    targetLane.tasks.unshift(this.withTaskStatus(task, status));
    this.syncPersistedLane(currentLane.status, currentLane.tasks);
    this.syncPersistedLane(targetLane.status, targetLane.tasks);
    this.taskDatas = [...this.taskDatas];
    this.cdr.markForCheck();
  }

  private syncPersistedLane(status: string, visibleTasks: ICardData[]) {
    const visibleIds = new Set(visibleTasks.map(task => task.taskId));
    this.persistedTaskDatas = this.persistedTaskDatas.map(lane => ({
      ...lane,
      tasks: lane.tasks.filter(task => !visibleIds.has(task.taskId)),
    }));

    this.persistedTaskDatas = this.persistedTaskDatas.map(lane => {
      if (lane.status !== status) {
        return lane;
      }
      return { ...lane, tasks: [...visibleTasks, ...lane.tasks] };
    });
  }

  openAssignee(assignee: TeamMember | null) {
    if (!assignee) {
      return;
    }
    this.router.navigate(
      [`/gp/${this.gpId}/vehicle/${this.vehicleId}/team/${assignee.id}`],
      { state: { member: assignee } }
    );
  }

  openSuggestTask() {
    if (this.createTaskDialogRef || this.taskDetailsDialogRef) {
      return;
    }

    this.managementService.setTaskDetails({
      vehicleId: this.vehicleId,
      gpId: this.gpId,
      isSuggest: true,
      topicList: this.topicList,
      assigneeList: this.assigneeList,
      isDialog: true,
    });

    this.createTaskDialogRef = this.dialog.open(TaskDetailsComponent, {
      width: 'min(620px, calc(100vw - 32px))',
      maxWidth: 'calc(100vw - 32px)',
      height: 'min(864px, calc(100vh - 32px))',
      maxHeight: 'calc(100vh - 32px)',
      panelClass: 'create-task-dialog-panel',
      backdropClass: 'create-task-dialog-backdrop',
      ariaLabel: 'Create task',
      autoFocus: 'first-tabbable',
    });
    this.createTaskDialogRef.afterClosed().subscribe(() => {
      this.createTaskDialogRef = undefined;
      this.managementService.setTaskDetails(null);
    });
  }
}

@Component({
  selector: 'app-task-card',
  templateUrl: './task-card.component.html',
  styleUrls: ['./management.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskCardComponent {
  @Input() cardData: ICardData;
  @Output() cardClickEvent = new EventEmitter<ICardData>();
  @Output() assigneeClickEvent = new EventEmitter<TeamMember>();


  clickHandler(value: ICardData) {
    this.cardClickEvent.emit(value);
  }

  assigneeClickHandler(event: Event, assignee: TeamMember | null) {
    event.stopPropagation();
    if (assignee) {
      this.assigneeClickEvent.emit(assignee);
    }
  }

  avatarColor(assignee: TeamMember): string {
    return avatarColorFor(assignee.id);
  }

  trackByMemberId(_index: number, assignee: TeamMember): string {
    return assignee.id;
  }
}

@Component({
  selector: 'app-task-details',
  templateUrl: './task-details.component.html',
  styleUrls: ['./management.component.scss'],
})
export class TaskDetailsComponent implements OnDestroy, OnInit {
  @ViewChild('taskConfirmDialog') private taskConfirmDialog?: ElementRef<HTMLDialogElement>;

  isLoading = false;
  vehicleId = '';
  gpId = '';
  taskId = '';
  taskDetail: ITaskDetailData | null = null;
  taskSummary: ITaskDetail | null = null;
  isSuggest = false;
  isDialogMode = false;
  isDialogTarget = false;
  suggestTaskForm!: FormGroup;
  editTaskForm!: FormGroup;
  commentForm!: FormGroup;
  isEditing = false;
  submitted = false;
  suggestTaskScrolled = false;
  suggestTaskAtBottom = false;
  isSubmittingComment = false;
  showCommentForm = false;
  isUploadingDocuments = false;
  isSyncingClickUp = false;
  selectedDocumentFiles: File[] = [];
  selectedSuggestFiles: File[] = [];
  pendingConfirmation: 'delete-task' | 'remove-document' | null = null;
  pendingDocument: ITaskDocument | null = null;
  openDetailSections: Record<DetailSection, boolean> = { ...DEFAULT_DETAIL_SECTIONS };
  detailCreatedBy: TeamMember | null = null;
  detailVisibleAssignees: TeamMember[] = [];
  detailChecklistItems: ITaskChecklistItem[] = [];
  detailChecklistCompleted = 0;
  detailChecklistProgress = 0;
  footerNotifyInApp = false;
  footerNotifyEmail = false;
  private taskFooterSnapshot = '';
  private taskChecklistDirty = false;
  detailActivityItems: Array<{
    event: ITaskActivity;
    user: TeamMember | null;
    actor: string;
    actorShort: string;
    actionLabel: string;
    dateLabel: string;
    timeLabel: string;
    file: ITaskDocument | null;
    checklistLabel: string;
    changeRows: Array<{ label: string; value?: string; from?: string; to?: string }>;
  }> = [];
  funds: ITopicListItem[] = [];
  assigneeList: TeamMember[] = [];
  private subscriptions = new Subscription();

  constructor(
    public managementService: ManagementService,
    private commonService: CommonService,
    private fb: FormBuilder,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    @Optional() private dialogRef: MatDialogRef<TaskDetailsComponent> | null
  ) {
    this.isDialogMode = !!dialogRef;
  }

  ngOnInit() {
    this.subscriptions.add(this.managementService.taskDetailsObservable$.subscribe(res => {
      this.resetDrawerState();
      if (!res) {
        this.isDialogTarget = false;
        return;
      }
      if (res?.isDialog && !this.isDialogMode) {
        this.isDialogTarget = true;
        return;
      }
      this.isDialogTarget = false;
      if (res && !res.isSuggest) {
        this.isSuggest = false;
        this.taskSummary = res;
        this.taskId = res.taskId ?? '';
        this.vehicleId = res.vehicleId ?? '';
        this.assigneeList = res.assigneeList ?? [];
        this.getTaskDetails();
      } else {
        this.isSuggest = true;
        this.vehicleId = res.vehicleId ?? '';
        this.gpId = res.gpId ?? '';
        this.funds = res.topicList ?? [];
        this.assigneeList = res.assigneeList ?? [];
        this.loadSubTaskForm();
      }
    }));
    this.subscriptions.add(this.managementService.openDrawerFlag.subscribe(isOpen => {
      if (!isOpen) {
        this.resetDrawerState();
      }
    }));

  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  getTaskDetails(publishListUpdate = false) {
    this.isLoading = true;
    this.taskDetail = null;
    this.managementService.getTaskDetail(this.vehicleId, this.taskId).subscribe({
      next: response => {
        this.taskDetail = response.data.task;
        if (publishListUpdate && this.taskDetail) {
          this.managementService.publishTaskListItemUpdate(
            this.taskId,
            this.taskDetail
          );
        }
        this.prepareTaskDetailView();
        this.loadEditTaskForm();
      },
      error: _error => {
        this.isLoading = false;
        this.commonService.errorNotification('Failed to retrieve task details');
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  loadSubTaskForm() {
    this.suggestTaskForm = this.fb.group({
      taskSource: ['native', [Validators.required]],
      taskTitle: ['', [Validators.required, Validators.minLength(2)]],
      taskFundId: [this.funds[0]?.id ?? '', [Validators.required]],
      taskAssigneeIds: [[]],
      taskPriority: ['', [Validators.required]],
      taskDueAt: [''],
      taskDesc: [''],
      taskChecklistItems: this.fb.array([this.fb.control('')]),
      taskNotifyInApp: [true],
      taskNotifyEmail: [false],
    });
    this.updateFundValidatorForSource();
  }
  get taskSource() {
    return this.suggestTaskForm.get('taskSource')!;
  }
  loadEditTaskForm() {
    this.editTaskForm = this.fb.group({
      taskTitle: [this.taskDetail?.title ?? this.taskSummary?.title ?? '', [Validators.required]],
      taskStatus: [this.taskDetail?.status ?? this.taskSummary?.status ?? 'To do', [Validators.required]],
      taskPriority: [(this.taskDetail?.priority ?? 'medium').toUpperCase(), [Validators.required]],
      taskDueAt: [this.toDateInputValue(this.taskDetail?.dueAt)],
      taskDesc: [this.taskDetail?.description ?? ''],
      taskAssigneeIds: [this.taskDetail?.assignees?.map(assignee => assignee.id) ?? []],
    });
    this.loadCommentForm();
  }
  get taskTitle() {
    return this.suggestTaskForm.get('taskTitle')!;
  }
  get taskFundId() {
    return this.suggestTaskForm.get('taskFundId')!;
  }
  get taskAssigneeIds() {
    return this.suggestTaskForm.get('taskAssigneeIds')!;
  }
  get taskPriority() {
    return this.suggestTaskForm.get('taskPriority')!;
  }
  get taskDueAt() {
    return this.suggestTaskForm.get('taskDueAt')!;
  }
  get taskDesc() {
    return this.suggestTaskForm.get('taskDesc')!;
  }
  get taskChecklistItems(): FormArray {
    return this.suggestTaskForm.get('taskChecklistItems') as FormArray;
  }
  get editTaskTitle() {
    return this.editTaskForm.get('taskTitle')!;
  }

  get commentTextControl() {
    return this.commentForm.get('commentText')!;
  }

  submitTask() {
    if (this.suggestTaskForm.invalid) {
      this.suggestTaskForm.markAllAsTouched();
      return;
    }

    if (this.suggestTaskForm.valid) {
      this.submitted = true;
      const formData = new FormData();
      const selectedAssigneeIds = [
        ...(this.suggestTaskForm.value?.taskAssigneeIds ?? []),
      ].filter(Boolean);
      const fallbackOwnerId = this.assigneeList.find(member => member.isCurrentUser)?.id ?? '';
      const ownerId = selectedAssigneeIds[0] ?? fallbackOwnerId;
      if (!ownerId) {
        this.submitted = false;
        this.commonService.errorNotification('Select an assignee to create the task.');
        return;
      }
      formData.append('source', this.suggestTaskForm.value?.taskSource);
      formData.append('title', this.suggestTaskForm.value?.taskTitle);
      formData.append('fundId', this.suggestTaskForm.value?.taskFundId);
      formData.append('assigneeIds', JSON.stringify([ownerId]));
      formData.append('participantIds', JSON.stringify(selectedAssigneeIds.slice(1)));
      formData.append('priority', this.suggestTaskForm.value?.taskPriority);
      formData.append('dueAt', this.suggestTaskForm.value?.taskDueAt);
      formData.append('description', this.suggestTaskForm.value?.taskDesc);
      formData.append(
        'notifyInApp',
        String(this.suggestTaskForm.value?.taskNotifyInApp)
      );
      formData.append(
        'notifyEmail',
        String(this.suggestTaskForm.value?.taskNotifyEmail)
      );
      formData.append(
        'checklistItems',
        JSON.stringify(
          (this.suggestTaskForm.value?.taskChecklistItems ?? [])
            .map((item: string) => item?.trim())
            .filter(Boolean)
        )
      );
      this.managementService
        .submitSuggestTask(this.vehicleId, formData, this.gpId, [...this.selectedSuggestFiles])
        .subscribe({
          next: response => {
            if (response) {
              this.commonService.successBigNotification('The task has been created.', 'It is now available on the task board.');
            }
          },
          error: error => {
            this.submitted = false;
            this.commonService.errorNotification(
              error?.error?.message ?? error?.message ?? 'Unable to create the task for this fund.'
            );
          },
          complete: () => {
            this.submitted = false;
            if (this.isDialogMode) {
              this.dialogRef?.close(true);
            } else {
              this.managementService.closeDrawer();
            }
          }
        });
    }
  }

  closeCreateDialog() {
    if (this.isDialogMode) {
      this.dialogRef?.close();
      return;
    }
    this.managementService.closeDrawer();
    this.cancelTask();
  }

  closeTaskDetailsView() {
    if (this.isDialogMode) {
      this.dialogRef?.close();
      return;
    }
    this.managementService.closeDrawer();
  }

  cancelTask() {
    this.resetDrawerState();
  }

  addChecklistItem() {
    this.taskChecklistItems.push(this.fb.control(''));
  }

  onSuggestTaskScroll(event: Event) {
    const element = event.target as HTMLElement | null;
    if (!element) {
      return;
    }
    const maxScrollTop = Math.max(0, element.scrollHeight - element.clientHeight);
    this.suggestTaskScrolled = element.scrollTop > 1;
    this.suggestTaskAtBottom = maxScrollTop <= 1 || element.scrollTop >= maxScrollTop - 1;
  }

  formatInputDate(value: string): string {
    const [year, month, day] = value.split('-');
    return year && month && day ? `${day}.${month}.${year}` : '';
  }

  removeChecklistItem(index: number) {
    if (this.taskChecklistItems.length <= 1) {
      this.taskChecklistItems.at(0).setValue('');
      return;
    }
    this.taskChecklistItems.removeAt(index);
  }

  selectedSuggestAssignees(): TeamMember[] {
    const ids = new Set(this.suggestTaskForm?.value?.taskAssigneeIds ?? []);
    return this.assigneeList.filter(person => ids.has(person.id));
  }

  selectedEditAssignees(): TeamMember[] {
    const ids = new Set(this.editTaskForm?.value?.taskAssigneeIds ?? []);
    return this.assigneeList.filter(person => ids.has(person.id));
  }

  onSuggestFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const nextFiles = Array.from(input.files ?? []);
    this.selectedSuggestFiles = [...this.selectedSuggestFiles, ...nextFiles];
    input.value = '';
  }

  onSuggestFilesDropped(event: DragEvent) {
    event.preventDefault();
    const nextFiles = Array.from(event.dataTransfer?.files ?? []);
    this.selectedSuggestFiles = [...this.selectedSuggestFiles, ...nextFiles];
  }

  removeSuggestFile(index: number) {
    this.selectedSuggestFiles.splice(index, 1);
  }

  private updateFundValidatorForSource() {
    const fundControl = this.suggestTaskForm.get('taskFundId');
    if (!fundControl) {
      return;
    }
    fundControl.setValidators([Validators.required]);
    fundControl.updateValueAndValidity({ emitEvent: false });
  }

  enableEdit() {
    this.isEditing = true;
    this.loadEditTaskForm();
  }

  cancelEdit() {
    this.isEditing = false;
    this.detailChecklistItems = this.cloneChecklistItems(this.taskDetail?.checklistItems ?? []);
    this.footerNotifyInApp = !!this.taskDetail?.notifyInApp;
    this.footerNotifyEmail = !!this.taskDetail?.notifyEmail;
    this.taskChecklistDirty = false;
    this.recalculateChecklistProgress();
    this.taskFooterSnapshot = this.taskFooterSignature();
    this.loadEditTaskForm();
  }

  openAssigneeProfile(assignee: TeamMember) {
    const gpMatch = this.router.url.match(/\/gp\/([^/]+)/);
    const gpId = gpMatch ? gpMatch[1] : this.activatedRoute.snapshot.params['gpId'];
    const vehicleId = this.vehicleId || this.activatedRoute.snapshot.params['vehicleId'];
    this.closeTaskDetailsView();
    this.router.navigate(
      [`/gp/${gpId}/vehicle/${vehicleId}/team/${assignee.id}`],
      { state: { member: assignee } }
    );
  }

  openMemberProfile(member: TeamMember | null, event?: Event) {
    event?.stopPropagation();
    if (!member) {
      return;
    }
    this.openAssigneeProfile(member);
  }

  latestActivityRelativeTime(): string {
    const latestActivity = this.detailActivityItems[0]?.event;
    return latestActivity?.createdAt ? this.relativeTime(latestActivity.createdAt) : '';
  }

  attachmentsCount(): number {
    return this.taskDetail?.documentsCount ?? this.taskDetail?.documents?.length ?? 0;
  }

  private resetDrawerState() {
    this.submitted = false;
    this.suggestTaskScrolled = false;
    this.suggestTaskAtBottom = false;
    this.isEditing = false;
    this.selectedDocumentFiles = [];
    this.selectedSuggestFiles = [];
    this.isUploadingDocuments = false;
    this.isSyncingClickUp = false;
    this.isSubmittingComment = false;
    this.showCommentForm = false;
    this.detailCreatedBy = null;
    this.detailVisibleAssignees = [];
    this.detailChecklistItems = [];
    this.detailChecklistCompleted = 0;
    this.detailChecklistProgress = 0;
    this.footerNotifyInApp = false;
    this.footerNotifyEmail = false;
    this.taskFooterSnapshot = '';
    this.detailActivityItems = [];
    this.openDetailSections = {
      ...DEFAULT_DETAIL_SECTIONS,
    };
  }

  openCommentForm() {
    this.showCommentForm = true;
    this.openDetailSections = {
      ...this.openDetailSections,
      comments: true,
    };
  }

  closeCommentForm() {
    this.showCommentForm = false;
    this.commentForm?.reset({ commentText: '' });
  }

  submitComment() {
    if (!this.commentForm?.valid || this.isSubmittingComment) {
      this.commentTextControl?.markAsTouched();
      return;
    }
    const commentText = String(this.commentTextControl.value || '').trim();
    if (!commentText) {
      this.commentTextControl.setValue('');
      this.commentTextControl.markAsTouched();
      return;
    }
    this.isSubmittingComment = true;
    this.managementService.createTaskComment(this.taskId, commentText).pipe(
      finalize(() => {
        this.isSubmittingComment = false;
      })
    ).subscribe({
      next: comment => {
        this.commonService.successBigNotification('Comment added.');
        if (this.taskDetail) {
          this.taskDetail = {
            ...this.taskDetail,
            comments: [...(this.taskDetail.comments ?? []), comment],
            commentsCount: (this.taskDetail.commentsCount ?? this.taskDetail.comments?.length ?? 0) + 1,
          };
        }
        this.commentForm.reset({ commentText: '' });
        this.showCommentForm = false;
      },
      error: () => {
        this.commonService.errorNotification('Failed to add comment');
      },
    });
  }

  private loadCommentForm() {
    this.commentForm = this.fb.group({
      commentText: ['', [Validators.required, Validators.maxLength(4000)]],
    });
  }

  private prepareTaskDetailView() {
    const detail = this.taskDetail;
    this.detailVisibleAssignees = (detail?.assignees ?? []).slice(0, TASK_ASSIGNEE_PREVIEW_LIMIT);
    this.detailChecklistItems = this.cloneChecklistItems(detail?.checklistItems ?? []);
    this.footerNotifyInApp = !!detail?.notifyInApp;
    this.footerNotifyEmail = !!detail?.notifyEmail;
    this.recalculateChecklistProgress();
    this.taskFooterSnapshot = this.taskFooterSignature();
    this.taskChecklistDirty = false;
    this.detailCreatedBy = this.findCreatedByUser(detail);
    this.detailActivityItems = (detail?.activity ?? []).map(event => ({
      event,
      user: event.user ?? null,
      actor: this.activityActor(event),
      actorShort: this.activityActorShort(event),
      actionLabel: this.activityActionLabel(event),
      dateLabel: this.formatDate(event.createdAt),
      timeLabel: this.formatTime(event.createdAt),
      file: this.activityDocument(event),
      checklistLabel: this.activityChecklistLabel(event),
      changeRows: this.activityChangeRows(event),
    }));
  }

  private cloneChecklistItems(items: ITaskChecklistItem[]): ITaskChecklistItem[] {
    return items.map((item, index) => ({
      ...item,
      clientId: item.clientId ?? item.id ?? `item-${index}`,
      title: this.checklistItemValue(item),
      completed: this.isChecklistItemComplete(item),
      status: this.isChecklistItemComplete(item) ? 'done' : 'open',
      orderIndex: item.orderIndex ?? index,
    }));
  }

  private recalculateChecklistProgress() {
    this.detailChecklistCompleted = this.detailChecklistItems.filter(item =>
      this.isChecklistItemComplete(item)
    ).length;
    this.detailChecklistProgress = this.detailChecklistItems.length
      ? Math.round((this.detailChecklistCompleted / this.detailChecklistItems.length) * 100)
      : 0;
  }

  private taskFooterSignature(): string {
    return JSON.stringify({
      notifyInApp: this.footerNotifyInApp,
      notifyEmail: this.footerNotifyEmail,
      checklistItems: this.checklistPayload(),
    });
  }

  private checklistPayload() {
    return this.detailChecklistItems
      .map((item, index) => {
        const title = this.checklistItemValue(item).trim();
        if (!title) {
          return null;
        }
        const completed = this.isChecklistItemComplete(item);
        return {
          ...(item.id ? { id: item.id } : {}),
          title,
          completed,
          status: completed ? 'done' : 'open',
          orderIndex: index,
        };
      })
      .filter((item): item is {
        id?: string;
        title: string;
        completed: boolean;
        status: string;
        orderIndex: number;
      } => item !== null);
  }

  private findCreatedByUser(detail: ITaskDetailData | null): TeamMember | null {
    const createdActivity = detail?.activity?.find(event =>
      (event.type || '').toLowerCase().includes('create')
    );
    return createdActivity?.user
      || detail?.activity?.find(event => event.user)?.user
      || null;
  }

  isSectionOpen(section: DetailSection): boolean {
    return this.openDetailSections[section] ?? false;
  }

  toggleSection(section: DetailSection) {
    this.openDetailSections = {
      ...this.openDetailSections,
      [section]: !this.isSectionOpen(section),
    };
  }

  toggleDraftChecklistItem(index: number) {
    const item = this.detailChecklistItems[index];
    if (!item) {
      return;
    }
    const completed = !this.isChecklistItemComplete(item);
    this.detailChecklistItems[index] = {
      ...item,
      completed,
      done: completed,
      status: completed ? 'done' : 'open',
    };
    this.taskChecklistDirty = true;
    this.recalculateChecklistProgress();
  }

  updateDraftChecklistTitle(index: number, event: Event) {
    const item = this.detailChecklistItems[index];
    if (!item) {
      return;
    }
    const input = event.target as HTMLInputElement;
    this.detailChecklistItems[index] = {
      ...item,
      title: input.value,
    };
    this.taskChecklistDirty = true;
    this.recalculateChecklistProgress();
  }

  addDraftChecklistItem() {
    this.detailChecklistItems = [
      ...this.detailChecklistItems,
      {
        clientId: `new-${Date.now()}-${this.detailChecklistItems.length}`,
        title: '',
        completed: false,
        status: 'open',
        orderIndex: this.detailChecklistItems.length,
      },
    ];
    this.openDetailSections = {
      ...this.openDetailSections,
      checklist: true,
    };
    this.recalculateChecklistProgress();
  }

  removeDraftChecklistItem(index: number) {
    this.detailChecklistItems = this.detailChecklistItems
      .filter((_item, itemIndex) => itemIndex !== index)
      .map((item, itemIndex) => ({ ...item, orderIndex: itemIndex }));
    this.taskChecklistDirty = true;
    this.recalculateChecklistProgress();
  }

  setFooterNotifyInApp(checked: boolean) {
    this.footerNotifyInApp = checked;
  }

  setFooterNotifyEmail(checked: boolean) {
    this.footerNotifyEmail = checked;
  }

  hasTaskFooterChanges(): boolean {
    return this.taskChecklistDirty || this.taskFooterSignature() !== this.taskFooterSnapshot;
  }

  saveTask() {
    if (!this.editTaskForm?.valid) {
      this.editTaskForm.markAllAsTouched();
      return;
    }
    if (this.submitted) {
      return;
    }
    const hasFooterChanges = this.hasTaskFooterChanges();
    if (!this.editTaskForm.dirty && !hasFooterChanges) {
      this.isEditing = false;
      return;
    }
    this.submitted = true;
    const formData = new FormData();
    this.appendDirtyEditField(formData, 'title', 'taskTitle');
    this.appendDirtyEditField(formData, 'status', 'taskStatus');
    this.appendDirtyEditField(formData, 'priority', 'taskPriority');
    this.appendDirtyEditField(formData, 'dueAt', 'taskDueAt');
    this.appendDirtyEditField(formData, 'description', 'taskDesc');
    const assigneeControl = this.editTaskForm.get('taskAssigneeIds');
    if (assigneeControl?.dirty) {
      formData.append(
        'assigneeIds',
        JSON.stringify(assigneeControl.value ?? [])
      );
    }
    if (hasFooterChanges) {
      formData.append('checklistItems', JSON.stringify(this.checklistPayload()));
      formData.append('notifyInApp', String(this.footerNotifyInApp));
      formData.append('notifyEmail', String(this.footerNotifyEmail));
    }
    this.managementService.updateTask(this.taskId, formData).subscribe({
      next: () => {
        this.commonService.successBigNotification('The task has been updated.');
      },
      error: () => {
        this.submitted = false;
        this.commonService.errorNotification('Failed to update task');
      },
      complete: () => {
        this.submitted = false;
        this.isEditing = false;
        this.getTaskDetails(true);
      }
    });
  }

  private appendDirtyEditField(formData: FormData, key: string, controlName: string) {
    const control = this.editTaskForm.get(controlName);
    if (control?.dirty) {
      formData.append(key, control.value ?? '');
    }
  }

  deleteTask() {
    if (this.submitted || this.pendingConfirmation) {
      return;
    }
    this.openConfirmation('delete-task');
  }

  private performDeleteTask() {
    this.submitted = true;
    this.managementService.deleteTask(this.taskId).subscribe({
      next: () => {
        this.commonService.successBigNotification('The task has been deleted.');
      },
      error: error => {
        this.submitted = false;
        this.commonService.errorNotification(this.deleteTaskErrorMessage(error));
      },
      complete: () => {
        this.submitted = false;
        this.closeTaskDetailsView();
      }
    });
  }

  private deleteTaskErrorMessage(error: unknown): string {
    const response = error as { status?: number; error?: unknown } | null;
    const message = this.readTaskErrorMessage(response?.error);
    const taskStatus = (this.taskDetail?.status ?? this.taskSummary?.status ?? '').toLowerCase();
    const normalizedMessage = message?.toLowerCase() ?? '';

    if (normalizedMessage.includes('in review') || (response?.status === 400 && taskStatus.includes('review'))) {
      return 'This task cannot be deleted while it is in review. Move it to another status first.';
    }
    if (normalizedMessage.includes('completed') || (response?.status === 400 && taskStatus.includes('completed'))) {
      return 'Completed tasks cannot be deleted. Reopen the task first.';
    }
    return message || 'Unable to delete this task. Please try again.';
  }

  private readTaskErrorMessage(value: unknown): string | undefined {
    if (typeof value === 'string') {
      try {
        return this.readTaskErrorMessage(JSON.parse(value));
      } catch {
        return value.trim() || undefined;
      }
    }
    if (Array.isArray(value)) {
      const messages = value
        .map(item => this.readTaskErrorMessage(item))
        .filter((item): item is string => !!item);
      return messages.length ? messages.join(', ') : undefined;
    }
    if (!value || typeof value !== 'object') {
      return undefined;
    }
    const body = value as { message?: unknown; error?: unknown };
    return this.readTaskErrorMessage(body.message) ?? this.readTaskErrorMessage(body.error);
  }

  openConfirmation(action: 'delete-task' | 'remove-document', document?: ITaskDocument) {
    this.pendingConfirmation = action;
    this.pendingDocument = document ?? null;
    setTimeout(() => {
      const dialog = this.taskConfirmDialog?.nativeElement;
      if (dialog && !dialog.open) {
        dialog.showModal();
      }
    });
  }

  cancelConfirmation() {
    this.taskConfirmDialog?.nativeElement.close();
    this.pendingConfirmation = null;
    this.pendingDocument = null;
  }

  confirmPendingAction() {
    const action = this.pendingConfirmation;
    const document = this.pendingDocument;
    this.cancelConfirmation();
    if (action === 'delete-task') {
      this.performDeleteTask();
    } else if (action === 'remove-document' && document) {
      this.performRemoveDocument(document);
    }
  }

  onDocumentFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.selectedDocumentFiles = Array.from(input.files ?? []);
    input.value = '';
  }

  uploadDocuments() {
    if (!this.selectedDocumentFiles.length || this.isUploadingDocuments) {
      return;
    }
    this.isUploadingDocuments = true;
    this.managementService
      .uploadTaskDocuments(this.taskId, this.selectedDocumentFiles)
      .pipe(finalize(() => {
        this.isUploadingDocuments = false;
      }))
      .subscribe({
        next: () => {
          this.commonService.successBigNotification('Document uploaded.');
          this.selectedDocumentFiles = [];
          this.getTaskDetails();
        },
        error: error => {
          console.warn('Task document upload failed', error);
          this.commonService.errorNotification(this.documentUploadErrorMessage(error));
        },
      });
  }

  removeDocument(document: ITaskDocument) {
    if (this.submitted || this.pendingConfirmation) {
      return;
    }
    this.openConfirmation('remove-document', document);
  }

  private performRemoveDocument(document: ITaskDocument) {
    this.managementService.removeTaskDocument(this.taskId, document.id).subscribe({
      next: () => {
        this.commonService.successBigNotification('Document removed.');
        this.getTaskDetails();
      },
      error: () => {
        this.commonService.errorNotification('Failed to remove document');
      },
    });
  }

  documentName(document: ITaskDocument): string {
    return document.currentVersion?.originalName || document.title;
  }

  documentMeta(document: ITaskDocument): string {
    const size = document.currentVersion?.sizeBytes
      ? this.formatBytes(document.currentVersion.sizeBytes)
      : '';
    const date = document.currentVersion?.createdAt
      ? this.formatDate(document.currentVersion.createdAt)
      : '';
    return [size, date].filter(Boolean).join(' · ');
  }

  selectedDocumentLabel(): string {
    if (!this.selectedDocumentFiles.length) {
      return 'No file selected';
    }
    if (this.selectedDocumentFiles.length === 1) {
      return this.selectedDocumentFiles[0].name;
    }
    return `${this.selectedDocumentFiles.length} files selected`;
  }

  activityTypeLabel(type?: string | null): string {
    if (!type) {
      return 'Activity';
    }
    const normalized = type.toLowerCase();
    const labelMap: Record<string, string> = {
      assigneeids: 'Assignees',
      dueat: 'Due date',
      tostatus: 'To',
      fromstatus: 'From',
      status: 'Status',
      taskupdated: 'Task updated',
    };
    if (labelMap[normalized]) {
      return labelMap[normalized];
    }
    return type
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[-_]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  activityActor(event: ITaskActivity): string {
    const user = event.user;
    if (!user) {
      return '';
    }
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return name || user.email || '';
  }

  activityActorShort(event: ITaskActivity): string {
    const user = event.user;
    if (!user) {
      return this.activityActor(event);
    }
    const firstName = user.firstName?.trim();
    const lastInitial = user.lastName?.trim()?.charAt(0);
    if (firstName && lastInitial) {
      return `${firstName} ${lastInitial}.`;
    }
    if (firstName) {
      return firstName;
    }
    return this.activityActor(event);
  }

  activityInitials(event: ITaskActivity): string {
    const name = this.activityActor(event);
    if (!name) {
      return 'A';
    }
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part.charAt(0).toUpperCase())
      .join('');
  }

  activityAvatarColor(event: ITaskActivity): string {
    return avatarColorFor(event.user?.id || event.id);
  }

  activityActionLabel(event: ITaskActivity): string {
    const normalized = (event.type || '').toUpperCase();
    const actionMap: Record<string, string> = {
      TASK_CREATED: 'added this task',
      TASK_DOCUMENT_ATTACHED: 'attached a file',
      TASK_ASSIGNED: 'was added as assignee',
      TASK_ASSIGNEES_UPDATED: 'updated assignees',
      TASK_COMMENT_CREATED: 'added a comment',
      TASK_UPDATED: 'updated this task',
      TASK_DELETED: 'deleted this task',
    };
    if (actionMap[normalized]) {
      return actionMap[normalized];
    }
    const message = event.message || this.activityTypeLabel(event.type);
    return message.charAt(0).toLowerCase() + message.slice(1);
  }

  activityDocument(event: ITaskActivity): ITaskDocument | null {
    const metadata = this.activityMetadata(event);
    const documentId = this.metadataString(metadata, 'documentId');
    const versionId = this.metadataString(metadata, 'versionId');
    return this.taskDetail?.documents?.find(document =>
      document.id === documentId || document.currentVersion?.id === versionId
    ) ?? null;
  }

  activityChecklistLabel(event: ITaskActivity): string {
    const metadata = this.activityMetadata(event);
    const keys = ['checklistLabel', 'checklistItem', 'checklistItemTitle', 'title', 'name', 'text', 'label'];
    for (const key of keys) {
      const value = this.metadataString(metadata, key);
      if (value) {
        return value;
      }
    }
    return (event.type || '').toUpperCase().includes('CHECKLIST')
      ? 'Checklist item'
      : '';
  }

  activityChangeRows(event: ITaskActivity): Array<{ label: string; value?: string; from?: string; to?: string }> {
    const metadata = this.activityMetadata(event);
    const normalizedType = (event.type || '').toUpperCase();
    if (normalizedType === 'TASK_DOCUMENT_ATTACHED') {
      return [];
    }

    const rows: Array<{ label: string; value?: string; from?: string; to?: string }> = [];
    const fromStatus = metadata['fromStatus'];
    const toStatus = metadata['toStatus'];
    if (fromStatus || toStatus) {
      rows.push({
        label: 'Status',
        from: fromStatus ? this.metadataValue('status', fromStatus) : '',
        to: toStatus ? this.metadataValue('status', toStatus) : '',
      });
    }

    Object.entries(metadata)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .filter(([key]) => !this.hiddenActivityMetadataKeys(fromStatus || toStatus).includes(key.toLowerCase()))
      .slice(0, 3)
      .forEach(([key, value]) => {
        rows.push({
          label: this.activityTypeLabel(key),
          value: this.metadataValue(key, value),
        });
      });

    const visibleRows = rows.slice(0, 3);
    if (visibleRows.length) {
      return visibleRows;
    }
    return this.fallbackActivityChangeRows(event);
  }

  private hiddenActivityMetadataKeys(hasStatusDiff: unknown): string[] {
    return [
      'documentid',
      'versionid',
      'source',
      'clickuptaskid',
      'clickupcommentid',
      'clickupsyncstatus',
      'fromstatus',
      'tostatus',
      ...(hasStatusDiff ? ['status'] : []),
    ];
  }

  private fallbackActivityChangeRows(event: ITaskActivity): Array<{ label: string; value?: string }> {
    const message = (event.message || '').trim();
    if (message && !['updated task', 'updated clickup task'].includes(message.toLowerCase())) {
      return [{ label: 'Details', value: message }];
    }
    return (event.type || '').toUpperCase() === 'TASK_UPDATED'
      ? [{ label: 'Details', value: 'Task fields changed' }]
      : [];
  }

  checklistItemValue(item: ITaskChecklistItem): string {
    return item.title || item.name || item.text || item.label || '';
  }

  isChecklistItemComplete(item: ITaskChecklistItem): boolean {
    const status = (item.status || '').toLowerCase();
    return !!item.completed || !!item.done || status === 'completed' || status === 'done';
  }

  relativeTime(dateString: string): string {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const diffMs = Date.now() - date.getTime();
    const minutes = Math.max(1, Math.round(diffMs / 60000));
    if (minutes < 60) {
      return `${minutes} min${minutes === 1 ? '' : 's'} ago`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 24) {
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    }
    const days = Math.round(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }

  activityMetadataRows(metadata: unknown): Array<{ key: string; value: string; isLong: boolean }> {
    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
      return [];
    }
    const data = metadata as Record<string, unknown>;
    const rows = Object.entries(data)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .filter(([key]) => key.toLowerCase() !== 'status')
      .map(([key, value]) => ({
        key: this.activityTypeLabel(key),
        value: this.metadataValue(key, value),
        isLong: key.toLowerCase() === 'assigneeids',
      }));
    return rows.slice(0, 3);
  }

  private activityMetadata(event: ITaskActivity): Record<string, unknown> {
    return event.metadata && typeof event.metadata === 'object' && !Array.isArray(event.metadata)
      ? event.metadata as Record<string, unknown>
      : {};
  }

  private metadataString(metadata: Record<string, unknown>, key: string): string {
    const value = metadata[key];
    return typeof value === 'string' ? value : '';
  }

  private metadataValue(key: string, value: unknown): string {
    if (key.toLowerCase() === 'assigneeids') {
      return this.assigneeNames(value);
    }
    if (typeof value === 'string') {
      const dateLabel = this.dateMetadataValue(value);
      if (dateLabel) {
        return dateLabel;
      }
      return this.activityTypeLabel(value);
    }
    if (Array.isArray(value)) {
      return value.map(item => this.metadataValue(key, item)).filter(Boolean).join(', ');
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    return JSON.stringify(value);
  }

  private assigneeNames(value: unknown): string {
    const ids = (Array.isArray(value) ? value : [value])
      .filter(Boolean)
      .map(String);
    const members = [...this.assigneeList, ...(this.taskDetail?.assignees ?? [])];
    const names = ids.map(id => {
      const member = members.find(person => person.id === id);
      if (!member) {
        return id.slice(0, 8);
      }
      return `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() || member.email || id.slice(0, 8);
    });
    return names.join(', ');
  }

  private dateMetadataValue(value: string): string {
    if (!/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return this.formatDate(value);
  }

  avatarColorFor(id: string): string {
    return avatarColorFor(id);
  }

  trackByMemberId(_index: number, member: TeamMember): string {
    return member.id;
  }

  trackByChecklistItem(index: number, item: ITaskChecklistItem): string | number {
    return item.clientId || item.id || index;
  }

  trackByDocumentId(_index: number, document: ITaskDocument): string {
    return document.id;
  }

  trackByActivityItem(_index: number, item: { event: ITaskActivity }): string {
    return item.event.id;
  }

  trackByCommentId(_index: number, comment: ITaskComment): string {
    return comment.id;
  }

  commentAuthorName(comment: ITaskComment): string {
    return memberDisplayName(comment.author);
  }

  commentAuthorShort(comment: ITaskComment): string {
    const author = comment.author;
    if (!author) {
      return 'Unknown';
    }
    const firstName = author.firstName?.trim();
    const lastInitial = author.lastName?.trim()?.charAt(0);
    if (firstName && lastInitial) {
      return `${firstName} ${lastInitial}.`;
    }
    return firstName || this.commentAuthorName(comment);
  }

  trackByMetadataRow(_index: number, row: { key: string }): string {
    return row.key;
  }

  trackByActivityChangeRow(_index: number, row: { label: string }): string {
    return row.label;
  }

  priorityLabel(priority: string | null | undefined): string {
    return formatPriorityLabel(priority);
  }

  priorityClass(priority: string | null | undefined): string {
    return taskPriorityClass(priority);
  }

  statusClass(status: string | null | undefined): string {
    return `status-${taskStatusSlug(status)}`;
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const day = String(date.getDate());
    const month = date.toLocaleString('en-US', { month: 'short' });
    return `${month} ${day}, ${date.getFullYear()}`;
  }

  formatDateTime(dateString: string): string {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${this.formatDate(dateString)} ${hours}:${minutes}`;
  }

  formatTime(dateString: string): string {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  isClickUpTask(): boolean {
    return this.taskDetail?.source === 'clickup' || !!this.taskDetail?.clickUp;
  }

  clickUpTaskId(): string {
    return this.taskDetail?.clickUp?.taskId || this.taskDetail?.externalId || '';
  }

  clickUpUrl(): string {
    return this.taskDetail?.clickUp?.url || this.taskDetail?.externalUrl || '';
  }

  clickUpSyncStatus(): string {
    return this.taskDetail?.clickUp?.syncStatus || this.taskDetail?.externalSyncStatus || '';
  }

  clickUpSyncError(): string {
    return this.taskDetail?.clickUp?.syncError || this.taskDetail?.externalSyncError || '';
  }

  clickUpStatusLabel(): string {
    const status = this.clickUpSyncStatus();
    return status ? this.activityTypeLabel(status) : 'Unknown';
  }

  canRetryClickUpSync(): boolean {
    return /_FAILED$/.test(this.clickUpSyncStatus());
  }

  canRefreshFromClickUp(): boolean {
    return this.isClickUpTask() && !this.taskDetail?.clickUp?.pending && !!this.clickUpTaskId();
  }

  retryClickUpSync() {
    if (!this.canRetryClickUpSync() || this.isSyncingClickUp) {
      return;
    }
    this.isSyncingClickUp = true;
    this.managementService.retryClickUpTaskSync(this.taskId).pipe(
      finalize(() => {
        this.isSyncingClickUp = false;
      })
    ).subscribe({
      next: () => {
        this.commonService.successBigNotification('ClickUp sync retry queued.');
      },
      error: () => {
        this.commonService.errorNotification('Failed to retry ClickUp sync');
      },
      complete: () => this.getTaskDetails(),
    });
  }

  refreshFromClickUp() {
    if (!this.canRefreshFromClickUp() || this.isSyncingClickUp) {
      return;
    }
    this.isSyncingClickUp = true;
    this.managementService.refreshClickUpTask(this.taskId).pipe(
      finalize(() => {
        this.isSyncingClickUp = false;
      })
    ).subscribe({
      next: () => {
        this.commonService.successBigNotification('Task refreshed from ClickUp.');
      },
      error: () => {
        this.commonService.errorNotification('Failed to refresh from ClickUp');
      },
      complete: () => this.getTaskDetails(),
    });
  }

  private toDateInputValue(dateString: string | null | undefined): string {
    return dateString ? new Date(dateString).toISOString().slice(0, 10) : '';
  }

  formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB'];
    let value = bytes / 1024;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }
    return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
  }

  private documentUploadErrorMessage(error: unknown): string {
    const uploadError = error as { status?: number; url?: string | null };
    if (uploadError.status === 0 && uploadError.url?.includes('amazonaws.com')) {
      return 'Upload blocked by S3 CORS. Allow this origin on the bucket.';
    }
    return 'Failed to upload document';
  }

  convertTextToLinks(text: string): string {
    const urlRegex = /([([{“"'])?(https?:\/\/[^\s)\]}“"']+)([)\]}”"'])?/g;
    let html = '';
    let lastIndex = 0;

    text.replace(urlRegex, (match, openChar, url, closeChar, offset) => {
        html += this.escapeHtml(text.slice(lastIndex, offset));
        const safeUrl = this.escapeAttribute(url);
        const displayText = `${openChar || ''}${url}${closeChar || ''}`;
        html += `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="sub-task-link">${this.escapeHtml(displayText)}</a>`;
        lastIndex = offset + match.length;
        return match;
    });

    return html + this.escapeHtml(text.slice(lastIndex));
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private escapeAttribute(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
